<template>
  <div class="customer-create main-content">
    <form action="" @submit.prevent="submitTemplateData">
      <div class="page-header">
        <h3 class="page-title">{{ isEdit ? $t('emails.edit_dynamic_template') : $t('emails.new_dynamic_template') }}</h3>
        <ol class="breadcrumb">
          <li class="breadcrumb-item">
            <router-link slot="item-title" to="/super-admin/dashboard">{{ $t('general.home') }}</router-link>
          </li>
          <li class="breadcrumb-item">
            <router-link slot="item-title" to="/super-admin/emailTemplates">{{ $tc('emails.dynamic_templates') }}</router-link>
          </li>
          <li class="breadcrumb-item">
            {{ isEdit ? $t('emails.edit_dynamic_template') : $t('emails.new_dynamic_template') }}
          </li>
        </ol>
        <div class="page-actions header-button-container">
          <base-button
            :loading="isLoading"
            :disabled="isSubmitDisabled"
            :tabindex="23"
            icon="save"
            color="theme"
            type="submit"
          >
            {{ isEdit ? $t('emails.update_template') : $t('emails.save_template') }}
          </base-button>
        </div>
      </div>
      <div class="customer-card card">
        <div class="card-body">
          <div class="row col-sm-12">
            <div class="col-sm-9">
              <div class="form-group">
                <label class="form-label">{{ $t('emails.template_name') }}</label><span class="text-danger"> *</span>
                <base-input
                  :invalid="$v.formData.name.$error"
                  v-model="formData.name"
                  focus
                  type="text"
                  name="name"
                  tab-index="1"
                  @input="$v.formData.name.$touch()"
                />
                <div v-if="$v.formData.name.$error">
                  <span v-if="!$v.formData.name.required" class="text-danger">
                    {{ $tc('validation.name_required') }}
                  </span>
                  <span v-if="!$v.formData.name.minLength" class="text-danger">
                    {{ $tc('validation.name_min_length', $v.formData.name.$params.minLength.min, { count: $v.formData.name.$params.minLength.min }) }}
                  </span>
                </div>
              </div>

              <div class="form-group">
                <label class="form-label">{{ $tc('emails.action_type',1) }}</label><span class="text-danger"> *</span>
                <base-select
                  v-model="action_id"
                  :options="emailActions"
                  :searchable="true"
                  :show-labels="false"
                  :tabindex="16"
                  :allow-empty="true"
                  :placeholder="$t('user_guides.select_type')"
                  label="name"
                  track-by="id"
                />
                <div v-if="$v.action_id.$error">
                  <span v-if="!$v.action_id.required" class="text-danger">
                    {{ $tc('validation.required') }}
                  </span>
                </div>
              </div>

              <div class="form-group">
                <label class="form-label">{{ $t('emails.subject') }}</label><span class="text-danger"> *</span>
                <base-input
                  :invalid="$v.formData.subject.$error"
                  v-model="formData.subject"
                  focus
                  type="text"
                  name="subject"
                  tab-index="1"
                  @input="$v.formData.subject.$touch()"
                />
                <div v-if="$v.formData.subject.$error">
                  <span v-if="!$v.formData.subject.required" class="text-danger">
                    {{ $tc('validation.required') }}
                  </span>
                  <span v-if="!$v.formData.subject.minLength" class="text-danger">
                    {{ $tc('validation.name_min_length', $v.formData.subject.$params.minLength.min, { count: $v.formData.subject.$params.minLength.min }) }}
                  </span>
                </div>
              </div>

              <div class="form-group">
                <label class="form-label">{{ $t('emails.html_content') }}</label><span class="text-danger"> *</span>
                <base-text-area
                  v-model.trim="formData.html_content"
                  :tabindex="20"
                  :placeholder="$t('emails.html_content')"
                  type="text"
                  rows="10"
                />
                <div v-if="$v.formData.html_content.$error">
                  <span v-if="!$v.formData.html_content.required" class="text-danger">
                    {{ $tc('validation.required') }}
                  </span>
                  <span v-if="!$v.formData.html_content.maxLength" class="text-danger">
                    {{ $tc('validation.htmlContent_max_length', $v.formData.html_content.$params.maxLength.max, { count: $v.formData.html_content.$params.maxLength.max }) }}
                  </span>
                </div>
              </div>

              <div class="form-group">
                <label class="form-label">{{ $t('emails.html_content_output') }}</label><br>
                <div v-html="formData.html_content"/>
              </div>
            </div>
            <div class="col-sm-3">
              <label class="form-label text-danger">{{ $t('emails.use_following_placeholder') }}</label><br>
              <ul>
                <li v-for="(placeholder, index) in placeholders" :key="index">
                  {{ placeholder }}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { validationMixin } from 'vuelidate'
const { required, minLength, maxLength } = require('vuelidate/lib/validators')

export default {
  mixins: [validationMixin],
  data () {
    return {
      isLoading: false,
      formData: {
        name: null,
        subject: null,
        html_content: null,
        action_id: null
      },
      action_id: null,
      emailActions: [],
      placeholders: []
    }
  },
  validations: {
    formData: {
      name: {
        required,
        minLength: minLength(3)
      },
      subject: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(255)
      },
      html_content: {
        required,
        maxLength: maxLength(1048576)
      }
    },
    action_id: {
      required
    }
  },
  computed: {
    isEdit () {
      if (this.$route.name === 'emailTemplates.edit') {
        return true
      }
      return false
    },
    isSubmitDisabled() {
      return this.$v.$invalid
    }
  },
  watch: {},
  mounted () {
    this.loadEmailActions()
    if (this.isEdit) {
      this.loadTemplate()
    } else {
      this.loadActionPlaceholder()
    }
  },
  methods: {
    ...mapActions('emailTemplates', [
      'addEmailTemplate',
      'updateEmailTemplate',
      'fetchEmailTemplate'
    ]),
    ...mapActions('emailActionList', [
      'fetchEmailActions'
    ]),
    async loadTemplate () {
      let res = await this.fetchEmailTemplate(this.$route.params.id)
      if (res.data.success) {
        this.formData.id = res.data.data.emailTemplate.id
        this.formData.name = res.data.data.emailTemplate.name
        this.formData.subject = res.data.data.emailTemplate.subject || ''
        this.formData.html_content = res.data.data.emailTemplate.html_content || ''
        this.action_id = res.data.data.emailActionTemplate.email_action
      }
    },
    async loadActionPlaceholder () {
      // For now, we'll use a default set of placeholders
      // In the future, this could be fetched from the backend based on selected action
      this.placeholders = [
        '{{company_name}}',
        '{{user_name}}',
        '{{invoice_number}}',
        '{{amount}}',
        '{{date}}'
      ]
    },
    async loadEmailActions () {
      let response = await this.fetchEmailActions({'pagination': 'no'})
      this.isRequestOngoing = false
      if (response.data && response.data.success === true) {
        this.emailActions = response.data.data.data
      }
    },
    async submitTemplateData () {
      this.$v.formData.$touch()
      if (this.$v.$invalid) {
        return true
      }
      if (this.action_id) {
        this.formData.action_id = this.action_id.id
      }
      if (this.isEdit) {
        this.isLoading = true
        try {
          let response = await this.updateEmailTemplate(this.formData)
          if (response.data.success) {
            window.toastr['success'](this.$t('emails.template_updated_message'))
            this.$router.push('/super-admin/emailTemplates')
            this.isLoading = false
            return true
          }
        } catch (err) {
        }
      } else {
        this.isLoading = true
        try {
          let response = await this.addEmailTemplate(this.formData)
          if (response.data.success) {
            window.toastr['success'](this.$t('emails.template_created_message'))
            this.$router.push('/super-admin/emailTemplates')
            this.isLoading = false
            return true
          }
        } catch (err) {}
      }
    }
  }
}
</script>
